import { HashtagSourcesApiResponse } from 'api/api.types';
import useAuth from 'providers/hooks/useAuth';
import React from 'react';
import Layout from 'src/components/layout/Layout';
import TopicHeaderV2 from 'src/page-components/topic/components/TopicHeaderV2';
import { GAP_SIZE_REM, getRemWidthFromColumns } from 'utils/global';
import { normalizeSource } from 'utils/normalizers/source';
import TopicContentNSI from './components/TopicContentNSI';
import { getTopicPageMetadata } from 'utils/metadata.utils';
import GetTheAppBanner from 'src/components/banner/GetTheAppBanner';
import {
  Flex,
  spacing,
  Link,
  color,
  CaretRightSvg,
  Box,
  Text,
  useIsMobileView,
  useColorMode,
} from 'deepstash-ui';
import { useRouter } from 'next/router';
import NextLink from 'src/components/navigation/NextLink';

interface PaginatedTopicPageProps {
  topicTag: string;
  hashtagApi: HashtagSourcesApiResponse;
}

const PaginatedTopicPage: React.FC<PaginatedTopicPageProps> = ({
  hashtagApi,
}) => {
  const { isLoggedIn } = useAuth();
  const isMobileView = useIsMobileView();
  const { colorMode } = useColorMode();
  const router = useRouter();
  const pageNumber = parseInt(router.query.page as string) ?? 1;
  const numberOfPages = Math.ceil(hashtagApi.meta.total_count / 5.0);
  const hashtagUrlParam = router.query.hashTag as string;

  const renderPages = () => {
    const arr: number[] = [];

    const PAGES_MAX_NAVIGATION = isMobileView ? 1 : 3;

    if (pageNumber > PAGES_MAX_NAVIGATION + 1) {
      arr.push(1);
    }
    if (pageNumber > PAGES_MAX_NAVIGATION + 2) {
      arr.push(-1);
    }
    for (
      let i = Math.max(1, pageNumber - PAGES_MAX_NAVIGATION);
      i <= Math.min(pageNumber + PAGES_MAX_NAVIGATION, numberOfPages);
      i++
    ) {
      arr.push(i);
    }
    if (pageNumber < numberOfPages - PAGES_MAX_NAVIGATION - 1) {
      arr.push(-1);
    }
    if (pageNumber < numberOfPages - PAGES_MAX_NAVIGATION) {
      arr.push(numberOfPages);
    }

    return (
      <Flex maxW="100%" alignSelf={'center'} my={GAP_SIZE_REM}>
        <NextLink
          href={
            router.asPath === '/topic/[hashTag]/2'
              ? '/topic/[hashTag]'
              : '/topic/[hashTag]/[page]'
          }
          visibility={pageNumber > 1 ? 'visible' : 'hidden'}
          as={
            router.asPath === '/topic/[hashTag]/2'
              ? `/topic/${hashtagUrlParam}`
              : `/topic/[${hashtagUrlParam}]/${pageNumber - 1}`
          }
        >
          <Link
            _hover={undefined}
            _focus={undefined}
            href={
              router.asPath === '/topic/[hashTag]/2'
                ? `/topic/${hashtagUrlParam}`
                : `/topic/${hashtagUrlParam}/${pageNumber - 1}`
            }
            color={color[colorMode].text}
            title={(pageNumber - 1).toString()}
          >
            <Flex
              transform={'rotate(180deg)'}
              _hover={{ cursor: 'pointer' }}
              px={spacing.XS.rem}
              py={spacing.toRem(6)}
              mr={spacing.XS.rem}
              borderRadius={spacing.L.rem}
              align={'center'}
              justifyContent={'center'}
              color={color[colorMode].textSecondary}
            >
              <CaretRightSvg />
            </Flex>
          </Link>
        </NextLink>

        {arr.map((element, index) =>
          element === -1 ? (
            <Box px={spacing.M.rem} py={spacing.XS.rem} mr={spacing.XS.rem}>
              <Text color={color[colorMode].text}>...</Text>
            </Box>
          ) : (
            <NextLink
              key={`pageIndex${index}`}
              href={
                element === 1 ? '/topic/[hashTag]' : '/topic/[hashTag]/[page]'
              }
              as={
                element === 1
                  ? `/topic/${hashtagUrlParam}`
                  : `/topic/${hashtagUrlParam}/${element}`
              }
            >
              <Box
                borderRadius={spacing.L.rem}
                px={spacing.M.rem}
                py={spacing.XS.rem}
                mr={spacing.XS.rem}
                _hover={{ cursor: 'pointer' }}
                backgroundColor={
                  element === pageNumber
                    ? color[colorMode].surface
                    : color[colorMode].top
                }
                _active={undefined}
                _focus={undefined}
              >
                <Link
                  _hover={undefined}
                  _focus={undefined}
                  href={
                    element === 1
                      ? `/topic/${hashtagUrlParam}`
                      : `/topic/${hashtagUrlParam}/${element}`
                  }
                  color={color[colorMode].text}
                  title={(element || '').toString()}
                >
                  {element}
                </Link>
              </Box>
            </NextLink>
          ),
        )}

        <NextLink
          href="/topic/[hashTag]/[page]"
          visibility={pageNumber < numberOfPages ? 'visible' : 'hidden'}
          as={`/topic/${hashtagUrlParam}/${pageNumber + 1}`}
        >
          <Link
            _hover={undefined}
            _focus={undefined}
            href={`/topic/${hashtagUrlParam}/${pageNumber + 1}`}
            color={color[colorMode].text}
            title={(pageNumber + 1).toString()}
          >
            <Flex
              px={spacing.XS.rem}
              py={spacing.toRem(6)}
              mr={spacing.XS.rem}
              borderRadius={spacing.L.rem}
              align={'center'}
              justifyContent={'center'}
              color={color[colorMode].textSecondary}
            >
              <CaretRightSvg />
            </Flex>
          </Link>
        </NextLink>
      </Flex>
    );
  };

  const allIdeasSection = {
    content: (
      <>
        <TopicHeaderV2
          width={{
            base: '100%',
            md: getRemWidthFromColumns(8),
          }}
          mb={GAP_SIZE_REM}
          hashtagSourcesApiResponse={hashtagApi}
          pageNumber={router.query.page as string}
        />
        <TopicContentNSI
          sources={hashtagApi.objects.map(apiSource =>
            normalizeSource(apiSource),
          )}
        />
        {renderPages()}
      </>
    ),
    metaContent: <GetTheAppBanner />,
    metaContentTopOffset: 96,
    /**
     * If logged in sticky the top stashers and related topics subsections
     * If not logged in sticky the sign up card
     * This prop is only for the desktop view
     */
    metaContentStickyOffset: isLoggedIn ? 32 : -688,
  };

  return (
    <Layout
      pageMetadata={getTopicPageMetadata({
        topicTitle: hashtagApi.hashtag,
        topicName: hashtagApi.hashtag,
        pageCount: router.query.page as string,
        pageTitle: hashtagApi.seo_title,
        pageDescription: hashtagApi.seo_description,
      })}
      isShortProAd
      sections={[allIdeasSection]}
    />
  );
};

export default PaginatedTopicPage;
